import type { Movie, Review, Video } from "./types";
import { constructPosterUrl, constructYoutubeIframeUrl } from "./utils";

export const createIndividualReviewDiv = (review: Review) => {
  const reviewDiv = document.createElement("div");
  reviewDiv.classList.add("review");

  const content = document.createElement("blockquote");
  content.textContent = review.content.slice(0, 250) + "...";

  const reviewAuthorInfo = document.createElement("div");
  reviewAuthorInfo.classList.add("author-info");

  const avatar = document.createElement("img");
  avatar.src = review.author_details.avatar_path
    ? `https://secure.gravatar.com/avatar${review.author_details.avatar_path}`
    : "";
  reviewAuthorInfo.appendChild(avatar);

  const title = document.createElement("h5");
  title.innerText = review.author_details.username || "Anonymous";
  reviewAuthorInfo.appendChild(title);

  reviewDiv.appendChild(reviewAuthorInfo);
  reviewDiv.appendChild(content);

  return reviewDiv;
};

export const createReviewsDiv = (reviews: Review[]) => {
  const reviewsDiv = document.createElement("div");
  reviewsDiv.classList.add("reviews");

  const heading = document.createElement("h4");
  heading.innerText = "Reviews";
  reviewsDiv.appendChild(heading);

  if (!reviews || reviews.length === 0) {
    const empty = document.createElement("p");
    empty.innerText = "No reviews.";
    reviewsDiv.appendChild(empty);
    return reviewsDiv;
  }

  reviews.forEach((review, index) => {
    if (index >= 3) return;

    const individualReviewDiv = createIndividualReviewDiv(review);
    reviewsDiv.appendChild(individualReviewDiv);
  });

  return reviewsDiv;
};

export const createYouTubeIframe = (video: Video) => {
  const url = constructYoutubeIframeUrl(video);

  if (!url) {
    return;
  }

  const iframeContainerDiv = document.createElement("div");
  iframeContainerDiv.classList.add("iframe-container");

  const iframe = document.createElement("iframe");
  iframe.classList.add("iframe");
  iframe.src = url;
  iframe.title = video.name || "YouTube video";
  iframe.allow =
    "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";

  iframeContainerDiv.appendChild(iframe);

  return iframeContainerDiv;
};

export const createVideosDiv = (videos: Video[]) => {
  const videosContainer = document.createElement("div");
  videosContainer.classList.add("videos");

  const heading = document.createElement("h4");
  heading.innerText = "Videos";
  videosContainer.appendChild(heading);

  const videosDiv = document.createElement("div");
  videosDiv.classList.add("videos__video-list");

  if (videos.length === 0 || !videos) {
    const noVideosText = document.createElement("p");
    noVideosText.innerText = "No Available videos";

    videosDiv.appendChild(noVideosText);
    videosContainer.appendChild(videosDiv);

    return videosContainer;
  }

  videos.forEach((video, index) => {
    if (index > 2) return;
    const newVideoDiv = createYouTubeIframe(video);
    if (newVideoDiv) videosDiv.appendChild(newVideoDiv);
  });

  videosContainer.appendChild(videosDiv);

  return videosContainer;
};

export const createSimilarMoviesDiv = (similarMovies: Movie[]) => {
  const similarMoviesDiv = document.createElement("div");
  similarMoviesDiv.classList.add("similar-movies");
  const heading = document.createElement("h4");
  heading.innerText = "Similar Movies";
  similarMoviesDiv.appendChild(heading);

  if (similarMovies.length === 0 || !similarMovies) {
    const noMoviesParagraph = document.createElement("p");
    noMoviesParagraph.innerText = "No available movies";

    similarMoviesDiv.appendChild(noMoviesParagraph);
  }

  similarMovies.forEach((similarMovie, index) => {
    if (index > 6) return;
    const movieSmallDiv = document.createElement("div");
    movieSmallDiv.classList.add("similar-movie");

    const movieSmallImage = document.createElement("img");
    movieSmallImage.src = constructPosterUrl(similarMovie.poster_path) || "";
    movieSmallImage.alt = similarMovie.title;
    movieSmallImage.width = 150;
    movieSmallImage.style.aspectRatio = "2/3";
    movieSmallImage.style.objectFit = "cover";

    movieSmallDiv.appendChild(movieSmallImage);

    similarMoviesDiv.appendChild(movieSmallDiv);
  });

  return similarMoviesDiv;
};

export const createMovieGridItem = (
  movieId: number,
  movieTitle: string,
  movieOverview: string,
  posterUrl: string | undefined,
  movieGenres: string[],
  movieYear: string | undefined,
  movieVoteAvg: number,
) => {
  const movieDiv = document.createElement("div");
  movieDiv.classList.add("grid-item");
  movieDiv.classList.add("movie");
  movieDiv.id = movieId.toString();

  const basicInfoDiv = document.createElement("div");
  basicInfoDiv.classList.add("movie__movie-basic-info");

  const infoDiv = document.createElement("div");
  infoDiv.classList.add("movie__movie-info");

  const movieTitleHeading = document.createElement("h2");
  movieTitleHeading.innerText = movieTitle;
  infoDiv.appendChild(movieTitleHeading);

  const movieYearSpan = document.createElement("span");
  movieYearSpan.classList.add("release-year");
  movieYearSpan.innerText = movieYear || "Not Available";
  infoDiv.appendChild(movieYearSpan);

  const movieOverviewParagraph = document.createElement("p");
  movieOverviewParagraph.classList.add("movie-description");
  movieOverviewParagraph.innerText = movieOverview;
  infoDiv.appendChild(movieOverviewParagraph);

  const movieGenreList = document.createElement("ul");
  movieGenres.forEach((genre: string) => {
    const movieGenreListItem = document.createElement("li");
    movieGenreListItem.innerText = genre;
    movieGenreList.appendChild(movieGenreListItem);
  });
  infoDiv.appendChild(movieGenreList);

  const movieVoteAvgSpan = document.createElement("span");
  movieVoteAvgSpan.classList.add("movie-avg-rating");
  movieVoteAvgSpan.innerText =
    movieVoteAvg > 0 ? movieVoteAvg.toFixed(2) : "N/A";
  infoDiv.appendChild(movieVoteAvgSpan);

  // movieDiv.appendChild(infoDiv);

  const mediaDiv = document.createElement("div");
  mediaDiv.classList.add("movie__movie-media");

  const movieImage = document.createElement("img");
  movieImage.setAttribute("fetchpriority", "high");
  movieImage.setAttribute("src", posterUrl || "");
  movieImage.setAttribute("alt", movieTitle);
  movieImage.setAttribute("height", "500");

  const additionalInfoPanel = document.createElement("div");
  additionalInfoPanel.classList.add("movie__movie-additional-info");

  mediaDiv.appendChild(movieImage);
  basicInfoDiv.appendChild(mediaDiv);
  basicInfoDiv.appendChild(infoDiv);
  movieDiv.prepend(basicInfoDiv);

  movieDiv.appendChild(additionalInfoPanel);
  return movieDiv;
};
